import React, { FunctionComponent, useMemo, useState } from 'react'
import {
  ActionBar,
  ActionBarActions,
  ActionBarMessage,
  Button,
  ButtonLink,
  Column,
  Columns,
  Header,
  PageHeader,
  Section,
} from '@lightspeed/design-system-react'
import { NoPermissionsPage } from '@vend/react-utilities'
import { T, useT } from '@transifex/react'
import {
  useCashMovementsManager,
  useRegisterClosureManager,
  useUserManager,
} from '../../ng-compat/hooks'
import { print } from '../print/print'
import { PERMISSIONS } from '../../constants'
import { isCashConcealedPaymentType } from '../../functions/payments'
import { OpenRegisterPanel } from './OpenRegisterPanel'
import { RegisterClosureSummary } from './RegisterClosureSummary'
import { useRegisterClosure } from './useRegisterClosure'
import { NOT_APPLICABLE } from './useFormatClosureNumber'
import { filterPaymentTypes } from './filterPaymentTypes'

interface OpenRegisterHeaderProps {
  printLastSummary?: () => void
  cashManagementEnabled?: boolean
  hasLastClosure?: boolean
  isPrinting?: boolean
}

const OpenRegisterHeader: FunctionComponent<OpenRegisterHeaderProps> = ({
  printLastSummary,
  cashManagementEnabled = false,
  hasLastClosure = false,
  isPrinting = false,
}) => {
  return (
    <>
      <PageHeader>
        <T _str="Open register" _context="Title for register open page" />
      </PageHeader>
      <ActionBar variant="page">
        <ActionBarMessage>
          {
            // Not adding context to these translations so the existing ones
            // in OpenRegisterPanel.tsx will be reused.
            cashManagementEnabled ? (
              <T _str="Set an opening float to open the register and make a sale." />
            ) : (
              <T _str="To make a sale, please open the register." />
            )
          }
        </ActionBarMessage>
        {printLastSummary && hasLastClosure ? (
          <ActionBarActions>
            <Button
              disabled={isPrinting}
              variant="supplementary"
              onClick={printLastSummary}
            >
              <T
                _str="Print last summary"
                _context="Print last register closure button"
              />
            </Button>
          </ActionBarActions>
        ) : null}
      </ActionBar>
    </>
  )
}

export const OpenRegister: FunctionComponent = () => {
  const registerClosureManager = useRegisterClosureManager()
  const canAccess = registerClosureManager.hasOpenClosePermission()
  const cashManagementEnabled = useCashMovementsManager().isCashManagementEnabled()
  const userManager = useUserManager()
  const [isPrinting, setIsPrinting] = useState(false)
  const lastClosure = useRegisterClosure({ type: 'last' })
  const t = useT()

  const summaryTotals = useMemo(() => {
    if (!lastClosure) {
      return {
        expectedTotal: NOT_APPLICABLE,
        countedTotal: NOT_APPLICABLE,
        differenceTotal: NOT_APPLICABLE,
        countedValid: true,
      }
    }

    const filteredClosure = {
      ...lastClosure,
      payments: filterPaymentTypes(lastClosure.payments, lastClosure.outlet.id),
    }

    return registerClosureManager.determineSummaryTotals(
      filteredClosure,
      filteredClosure.payments.some(isCashConcealedPaymentType)
    )
  }, [lastClosure, registerClosureManager])

  const reportUrl = `/register/closure/summary/${lastClosure?.register_closure_id}`
  const hasLastClosure = Boolean(lastClosure)

  const canViewReport = userManager.hasPermission(
    PERMISSIONS.REPORTING_REGISTER_CLOSURE
  )

  const printLastSummary = async () => {
    if (isPrinting || !lastClosure) {
      return
    }
    setIsPrinting(true)
    await print({
      type: 'register-closure',
      data: {
        ...registerClosureManager.generateRegisterClosurePrintData(lastClosure),
        summaryTotals,
      },
    })
    setIsPrinting(false)
  }

  if (!canAccess) {
    return (
      <>
        <OpenRegisterHeader cashManagementEnabled={cashManagementEnabled} />
        <Section className="vd-pt10">
          <NoPermissionsPage
            copy={t(
              'You don’t have permission to open the register. Switch to a user with permission.',
              { _context: 'Permission error page' }
            )}
            promptToSwitchUser={true}
            backgroundColor="transparent"
          />
        </Section>
      </>
    )
  }

  return (
    <>
      <OpenRegisterHeader
        printLastSummary={printLastSummary}
        cashManagementEnabled={cashManagementEnabled}
        hasLastClosure={hasLastClosure}
        isPrinting={isPrinting}
      />
      <Section category="tertiary">
        <OpenRegisterPanel minimal={true} />
      </Section>
      <RegisterClosureSummary type="last" totals={summaryTotals} />
      {lastClosure?.note ? (
        <Section>
          <Columns space={10} collapseBelow="tablet">
            <Column width="3/12">
              <Header level="2" type="sub-heading" className="vd-mb5">
                <T _str="Last Closing Summary" />
              </Header>
            </Column>
            <Column width="9/12">
              <div className="vd-field">
                <div className="vd-label">
                  <T _str="Note" />
                </div>
                <div className="vd-value">
                  <div className="wr-register-closure-notes wr-honour-line-breaks">
                    <p className="wr-info-field-value vd-p">
                      {lastClosure.note}
                    </p>
                  </div>
                </div>
              </div>
            </Column>
          </Columns>
        </Section>
      ) : null}
      <Section>
        <div className="vd-btn-group">
          {hasLastClosure ? (
            <Button
              disabled={isPrinting}
              variant="supplementary"
              onClick={printLastSummary}
            >
              <T
                _str="Print last summary"
                _context="Print last register closure button"
              />
            </Button>
          ) : null}
          {canViewReport && reportUrl ? (
            <ButtonLink href={reportUrl} variant="supplementary">
              <T
                _str="View Register Closure Sales"
                _context="Link to last register summary report"
              />
            </ButtonLink>
          ) : null}
        </div>
      </Section>
    </>
  )
}
